


import { CommonModule, Location } from '@angular/common';
import { Component, ViewChild, ElementRef, NgZone, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule, ActivatedRoute, Router, NavigationStart } from '@angular/router';
import { Subscription } from 'rxjs';
import { AttachmentDeleteRequest, FileInfo } from '@models/attachment.model'
import { OrderFilesProfileQueryResponseInfo } from '@models/order-files.model'
import { UserProfilesSearcQueryResponseInfo, } from "@models/user-profiles.model"
import { CustomerService } from "@services/customer/customer-service";
import { DecomUploadService } from '@services/decom-upload/decom-upload-service'
import { OrderService } from '@services/order/order-service';
import { InputLimit } from '@utils/input-limit';
import { JapaneseLabels } from '@utils/japaneseLabels';

/**
 * declare tife of UploadingFile 
 */
interface UploadingFile {
  file: File;
  name: string;
  progress: number;
  xhr?: XMLHttpRequest;
  error?: string | null;
}
/**
 * XRayNew component.
 * Handles the display and logic for creating a new X-ray entry,
 * including navigation, input limits, and route labels.
 */
@Component({
  selector: 'app-x-ray-new',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './x-ray-new.html',
  styleUrl: './x-ray-new.scss',
})
export class XRayNew {
  /** Utility for input length limiting */
  inputLimit = new InputLimit();
  /** Japanese text labels for routes */
  japaneseLabels = JapaneseLabels;

  /** Reference to the load more sentinel element for infinite scroll */
  @ViewChild("loadMoreSentinel", { static: false }) sentinel!: ElementRef;
  /** IntersectionObserver instance for lazy loading or infinite scroll */
  observer!: IntersectionObserver;
  /** Loading state for data fetching */
  loading: boolean = false;
  uploadLoading: boolean = false;

  userId !: string
  userList: UserProfilesSearcQueryResponseInfo[] = []
  // eslint-disable-next-line no-restricted-syntax
  profile_id: string = ''
  total_files: number = 0; // Track uploaded file count
  errorMessage: string | null = null;

  allowedExtensions = ['dcm'];//allow file name dcom
  maxFileSizeMB = 80; // Maximum allowed file size
  fileList: OrderFilesProfileQueryResponseInfo[] = []
  hospital_name!: string  //input hospital name by user
  selectedUserName: string | null = null; // Display selected user's name
  uploadProgress: number = 0; // display file upload percentage
  uploadingFiles: UploadingFile[] = []; // local uploads with progress
  fileName: string | null = null
  isOrderCreated: boolean = false;
  navigationSubscription: Subscription;
  goingToPreviewPage: boolean = false; // ✅ New flag
  mainProfile_id !: string;
  selectedProfileId !: string;
  hospitalName !: string;
  userSelectError: boolean = false; // Show error if no user selected
  contentType: string | null = null;


  /**
   * Constructs the XRayNew component.
   * 
   * @param route ActivatedRoute for accessing route parameters
   * @param location Angular Location service for navigation
   * @param decomUploadService Angular decomUploadService service for navigation
   * @param customerService Angular customerService service for navigation
   * @param orderService Angular orderService service for navigation
   * @param router Angular router add for navigation
   * @param ngZone An injectable service for executing work inside or outside of the Angular zone.
   */
  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private decomUploadService: DecomUploadService,
    private customerService: CustomerService,
    private orderService: OrderService,

    private router: Router,
    private ngZone: NgZone
  ) {
    // Listen for Angular route changes
    this.navigationSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.handlePageLeave();
      }
    });
  }
  customerData!: UserProfilesSearcQueryResponseInfo
  /**
   * Angular lifecycle hook.
   * Fetches the initial customer list user id wise on component initialization.
   */
  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.userId = params['user_id']
    });
    this.customerService.getCustomersByUserId(this.userId).subscribe({
      next: (response) => {
        this.userList = response.profiles
        // eslint-disable-next-line no-restricted-syntax
        this.mainProfile_id = this.userList.length > 0 ? this.userList[0].profile_id : '';

      },
      error: (err) => {
        console.error("Error", err);
      }
    });

    this.customerData = history.state.customer

    // eslint-disable-next-line @typescript-eslint/typedef
    const state = history.state;
    if (state && (state.customerData || state.fileList)) {
      this.customerData = state.customerData;
      this.fileList = state.fileList;
      this.hospital_name = state.hospitalName;
      this.profile_id = state.profile_id;
      this.selectedUserName = state.selectedUserName;
    }



  }

  // ✅ Browser Close / Refresh Warning
  @HostListener('window:beforeunload', ['$event'])
  /**
   * Browser beforeunload handler.
   *
   * Shows a confirmation dialog when the user attempts to close, refresh,
   * or navigate away from the page while there are uploaded files that would be deleted.
   * Prevents accidental loss of uploaded files when an order has not been created
   * and the user is not intentionally navigating back to the upload page.
   * 
   *  @param event The BeforeUnloadEvent provided by the browser
   */
  // eslint-disable-next-line jsdoc/require-jsdoc
  beforeUnloadHandler(event: BeforeUnloadEvent) {
    if (this.fileList.length > 0 && !this.isOrderCreated && !this.goingToPreviewPage) {
      event.preventDefault();
      event.returnValue = 'アップロードされたファイルは削除されます。本当にこのページを離れますか？';
    }
  }


  /**
   * Handle navigation inside the app (e.g. router navigation away from this component).
   *
   * If there are uploaded files and no order has been created, this should trigger
   * the cleanup/confirmation flow so files are not left orphaned on the server.
   */
  handlePageLeave() {
    if (this.fileList.length > 0 && !this.isOrderCreated && !this.goingToPreviewPage) {
      const confirmLeave: boolean = confirm('アップロードされたファイルは削除されます。このページを離れてもよろしいですか？');
      if (!confirmLeave) {
        this.router.navigateByUrl(this.router.url); // Stay on page
        return;
      }

      this.deleteAllUploadedFiles();
    }
  }

  /**
   * Delete all uploaded files from the server.
   *
   * Filters and maps this.fileList to the expected request shape and calls the
   * decomUploadService to delete files. Errors are logged and handled via subscription.
   */
  deleteAllUploadedFiles() {
    if (this.fileList.length === 0) return;

    const filesToDelete: FileInfo[] = this.fileList.map(file => ({
      file_id: file.file_id,
      file_path: file.delete_path!
    }));

    const deleteRequest: AttachmentDeleteRequest = {
      order_record_id: undefined, // No order yet
      files: filesToDelete
    };

    this.decomUploadService.deleteUploadFile(deleteRequest).subscribe({
      next: (res) => {
        console.log('All uploaded files deleted on page leave:', res);
      },
      error: (err) => {
        console.error('Failed to delete files on page leave:', err);
      }
    });
  }



  /**
   *Angular lifecycle hook.
   */
  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }




  /**
   * Handles file upload using a presigned S3 URL.
   *
   * Flow:
   * 1. Get the selected file from the input element.
   * 2. Request a presigned upload URL from the backend via `DecomUploadService`.
   * 3. Upload the file directly to S3 using the presigned URL.
   * 4. Show success or error messages depending on the result.
   *
   * @param event The file input change event  
   */
  upload(event: Event): void {
    this.uploadingFiles = [];
    this.errorMessage = null


    const input: HTMLInputElement = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      this.errorMessage = 'ファイルが選択されていません';
      return;
    }

    // Add all selected files to uploadedFiles array
    const selectedFiles: File[] = Array.from(input?.files);
    // ✅ Ensure we do not exceed 5 files after adding new files
    if (this.total_files + selectedFiles.length > 5) {
      this.errorMessage = `アップロードできるファイルは最大5件までです`;
      return;
    }
    const invalidFiles: string[] = [];
    for (let i: number = 0; i < input.files.length; i++) {
      const file: File = input.files[i];
      // ✅ Check extension
      const fileExtension: string | undefined = file.name.split('.').pop()?.toLowerCase();
      if (!fileExtension || !this.allowedExtensions.includes(fileExtension)) {
        invalidFiles.push(file.name + ' (invalid extension)');
        continue;
      }

      // ✅ Check file size
      const fileSizeMB: number = file.size / (1024 * 1024);
      if (fileSizeMB > this.maxFileSizeMB) {
        invalidFiles.push(file.name + ` (${Math.ceil(fileSizeMB)}MB exceeds 80MB)`);
      }
    }

    if (invalidFiles.length > 0) {
      this.errorMessage = `アップロードできないファイル: ${invalidFiles.join(', ')}`;
      // eslint-disable-next-line no-restricted-syntax
      input.value = ''; // Reset file input
      return;
    }


    this.errorMessage = null;
    this.uploadedFiles.push(...selectedFiles);

    // Now upload files (assuming you handle one by one for presigned URL)
    selectedFiles.forEach(file => {

      const fileWithProgress: UploadingFile = {
        file,
        name: file.name,
        progress: 0,
        xhr: undefined,
        error: null
      };

      // show this file in UI
      this.uploadingFiles.push(fileWithProgress);

      const fileName: string = file.name || 'file-name';
      const contentType: string = file.type || 'application/octet-stream';


      const uploadData: { profile_id: string; files: { filename: string; content_type: string; file_size_mb: number; }[]; } = {
        profile_id: this.mainProfile_id, // update profile ID dynamically
        files: [
          {
            filename: fileName,
            content_type: contentType,
            file_size_mb: Math.ceil(file.size / (1024 * 1024))
          }
        ]
      };

      this.uploadLoading = true;

      this.decomUploadService.getUploadUrl(uploadData).subscribe({
        next: (response) => {
          const presignedUrl: string = response.attachment_path?.[0];

          if (!presignedUrl) {
            console.error("No presigned URL returned");
            return;
          }

          // Use XMLHttpRequest for upload with progress
          const req: XMLHttpRequest = new XMLHttpRequest();
          req.upload.onprogress = (event: ProgressEvent) => {
            if (event.lengthComputable) {
              this.uploadProgress = Math.round((event.loaded / event.total) * 100);
              this.fileName = file.name
              this.contentType = file.type || 'application/octet-stream';

              this.ngZone.run(() => {
                fileWithProgress.progress = this.uploadProgress;  // ✅ update the file’s own progress
              });
            }
          };

          req.onload = () => {
            this.ngZone.run(() => {
              if (req.status === 200) {
                this.uploadProgress = 100; // reset after upload
                // eslint-disable-next-line no-restricted-syntax
                input.value = '';

                // ✅ Check if all files are done
                const allDone: boolean = this.uploadingFiles.every(f => f.progress === 100);
                if (allDone) {
                  this.uploadLoading = false;
                  // ✅ Call API again to refresh file list
                  this.decomUploadService.getFileListByProfileId(this.mainProfile_id!).subscribe({
                    next: (response) => {
                      this.fileList = response.order_files;
                      this.total_files = response.total_count;

                    },
                    error: (error) => {
                      console.error("Failed to refresh file list:", error);
                    }
                  });
                } else {
                  console.error("Upload failed:", req.statusText);
                  this.uploadLoading = false;
                }
              }
            });
          };

          req.onerror = () => {
            console.error("Upload error for", file.name);
            this.loading = false;
          };

          req.open("PUT", presignedUrl, true);
          req.setRequestHeader("Content-Type", contentType);
          req.send(file);
        },
        error: (error) => {
          console.error('Failed to get upload URL:', error);
          this.loading = false;
        }
      });
    });

  }
  uploadedFiles: File[] = [];
  disableDeleteButton: boolean = false;


  /**
   * Removes a file from the uploadedFiles array by index.
   * 
   * @param index The index of the file to remove
   */
  deleteFile(index: number): void {

    // Prevent multiple submissions
    if (this.loading) {
      console.warn("Delete operation is already in progress.");
      return;
    }
    // if (!this.mainProfile_id) {
    //   this.errorMessage = 'ユーザーを選択してください';
    //   return;
    // }

    const file: OrderFilesProfileQueryResponseInfo = this.fileList[index];
    if (!file || !file.delete_path) {
      console.error("Invalid file data:", file);
      return;
    }

    if (!file.delete_path) {
      console.error("File path is missing, cannot delete");
      return;
    }
    this.loading = true; // Disable further delete actions
    const deleteRequest: AttachmentDeleteRequest = {
      // file_id: file.file_id,
      files: [{
        file_id: file.file_id,
        file_path: file.delete_path
      }],  // Assuming attachment_path contains the storage path
      order_record_id: file.order_record_id || undefined
    };
    // if (!confirm(`ファイル「${file.filename}」を削除してもよろしいですか？`)) {
    //   return;
    // }

    this.loading = true; // Disable further delete actions

    this.decomUploadService.deleteUploadFile(deleteRequest).subscribe({
      next: (response) => {
        console.log('Delete success:', response);

        // ✅ Refresh file list
        this.decomUploadService.getFileListByProfileId(this.mainProfile_id!).subscribe({
          next: (response) => {
            this.fileList = response.order_files;
            this.total_files = response.total_count;
            console.log("Updated File List:", this.fileList);
            this.loading = false;
          },
          error: (error) => {
            console.error("Failed to refresh file list:", error);
            this.loading = false;
          }
        });
      },
      error: (error) => {
        console.error('Failed to delete file:', error);
        this.loading = false;
      }
    });
  }

  /**
   * Navigates back to the previous page.
   */
  back() {
    this.router.navigate(['dashboard/customer-list'])
  }

  /**
   * Handle change event from the user select element.
   *
   * - Reads the selected profile ID from the event target and assigns it to profile_id.
   * - If no selection is made, clears selectedUserName and sets an error message.
   * - If a matching user is found in userList, sets selectedUserName for display.
   *
   * @param event The change event from the select element
   */
  onUserChange(event: Event) {
    this.userSelectError = false; // Clear error if valid selection
    const selectedId: string = (event.target as HTMLSelectElement).value;
    this.profile_id = selectedId;

    const user: UserProfilesSearcQueryResponseInfo | undefined = this.userList.find(u => u.profile_id === selectedId);
    if (user) {
      this.selectedUserName = `${user.first_name} ${user.last_name}`;
      console.log("Selected User Name:", this.selectedUserName);
      this.userSelectError = false; // Clear error if valid selection
    } else {
      console.log("Selected User d:", this.selectedUserName = null);
    }
  }

  /**
   * Navigate to the X-ray detail page.
   *
   * Passes current customerData, fileList, hospital name, profile_id and selectedUserName
   * via router state so the detail page can access them.
   */
  goToDetails() {

    if (!this.profile_id) {
      this.userSelectError = true;  // show error
      return;
    }
    this.userSelectError = false; // reset error if valid

    this.goingToPreviewPage = true; // Set flag to true when navigating to details
    this.router.navigate(['dashboard/x-ray-detail'], {
      state: {
        customerData: this.customerData,
        fileList: this.fileList,
        hospitalName: this.hospital_name,
        profile_id: this.profile_id,
        selectedUserName: this.selectedUserName
      }
    });
  }





  isDragOver: boolean = false;
  /**
   * Handles files dropped via drag & drop.
   * Reuses the same validation and upload logic as file input.
   *
   * @param fileList The FileList from the drop event
   */
  private handleDroppedFiles(fileList: FileList): void {

    if (!fileList || fileList.length === 0) {
      this.errorMessage = 'ファイルが選択されていません';
      return;
    }
    // Reuse the same upload logic by simulating an input change event
    const mockEvent: Event = {
      target: { files: fileList }
    } as unknown as Event;

    this.upload(mockEvent);
  }
  /**
   * Drag over handler – prevents default to allow drop.
   *
   * @param event DragEvent
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }
  /**
   * Drag enter handler.
   *
   * @param event DragEvent
   */
  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }
  /**
   * Drag leave handler – only reset when actually leaving the drop zone.
   *
   * @param event DragEvent
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const target: HTMLElement | null = event.currentTarget as HTMLElement | null;
    const relatedTarget: Node | null = event.relatedTarget as Node | null;

    // Only reset drag-over state if the mouse actually left the drop zone
    if (target && relatedTarget && !target.contains(relatedTarget)) {
      this.isDragOver = false;
    }
  }
  /**
   * Drop handler – processes dropped files.
   *
   * @param event DragEvent
   */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files: FileList | undefined = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleDroppedFiles(files);
    }
  }
}